import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserPlus,
  Loader2,
  Trash2,
  Crown,
  Mail,
  ChevronDown,
  LogOut,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { notify } from '../lib/toast';
import { api } from '../lib/api';
import type { ProjectMemberOwner, ProjectMemberEntry, MemberRole } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';

// ── Role label helpers ─────────────────────────────────────────────────────────

// Built with `t` inside MemberRow (the only consumer) so labels stay localized.
function getRoleLabels(t: (key: string, fallback: string) => string): Record<MemberRole | 'owner', string> {
  return {
    owner: t('shareModal.roleOwner', 'Dono'),
    editor: t('shareModal.roleEditor', 'Editor'),
    commenter: t('shareModal.roleCommenter', 'Comentarista'),
    viewer: t('shareModal.roleViewer', 'Visualizador'),
  };
}

const ROLE_COLORS: Record<MemberRole | 'owner', string> = {
  owner: 'bg-primary/15 text-primary border-primary/20',
  editor: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20',
  commenter: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
  viewer: 'bg-muted text-muted-foreground border-border',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  invited: 'Aguardando',
  declined: 'Recusado',
};

// ── ShareModal ────────────────────────────────────────────────────────────────

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectTitle?: string;
  isOwner: boolean;
}

export function ShareModal({
  open,
  onOpenChange,
  projectId,
  projectTitle,
  isOwner,
}: ShareModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [owner, setOwner] = useState<ProjectMemberOwner | null>(null);
  const [members, setMembers] = useState<ProjectMemberEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('editor');

  const loadMembers = useCallback(async () => {
    if (!projectId) return;
    setIsFetching(true);
    try {
      const res = await api.getMembers(projectId);
      setOwner(res.owner);
      setMembers(res.members);
    } catch {
      notify.error(t('shareModal.loadMembersError', 'Erro ao carregar colaboradores'));
    } finally {
      setIsFetching(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open, loadMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsLoading(true);
    try {
      await api.inviteMember(projectId, { email: inviteEmail.trim(), role: inviteRole });
      // cerimônia: duas penas para a mesma história
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'twoquills', email: inviteEmail.trim() } }));
      setInviteEmail('');
      await loadMembers();
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || t('shareModal.inviteError', 'Erro ao enviar convite');
      notify.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: MemberRole) => {
    try {
      await api.updateMemberRole(projectId, memberId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
      notify.success(t('shareModal.roleUpdated', 'Papel atualizado'));
    } catch {
      notify.error(t('shareModal.roleUpdateError', 'Erro ao atualizar papel'));
    }
  };

  const handleRemove = async (memberId: string, email: string) => {
    try {
      await api.removeMember(projectId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      notify.success(t('shareModal.memberRemoved', '{{email}} removido', { email }));
    } catch {
      notify.error(t('shareModal.removeMemberError', 'Erro ao remover colaborador'));
    }
  };

  const handleLeave = async () => {
    // Current user's member entry
    const myEntry = members.find((m) => m.userId === user?.id);
    if (!myEntry) return;
    try {
      await api.removeMember(projectId, myEntry.id);
      notify.success(t('shareModal.leftProject', 'Você saiu do projeto'));
      onOpenChange(false);
    } catch {
      notify.error(t('shareModal.leaveProjectError', 'Erro ao sair do projeto'));
    }
  };

  const isMember = !isOwner && members.some((m) => m.userId === user?.id && m.status === 'active');

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {t('shareModal.title', 'Compartilhar Projeto')}
          </ResponsiveModalTitle>
          {projectTitle && (
            <ResponsiveModalDescription>
              {projectTitle}
            </ResponsiveModalDescription>
          )}
        </ResponsiveModalHeader>

        <div className="space-y-4 mt-2">
          {/* Invite form — only for owners */}
          {isOwner && (
            <form onSubmit={handleInvite} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="invite-email" className="text-sm font-medium">
                  {t('shareModal.inviteByEmail', 'Convidar por e-mail')}
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="colaborador@email.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="pl-9 h-9 text-sm"
                      disabled={isLoading}
                    />
                  </div>
                  <Select
                    value={inviteRole}
                    onValueChange={(v) => setInviteRole(v as MemberRole)}
                  >
                    <SelectTrigger className="w-36 h-9 text-sm">
                      <SelectValue />
                      <ChevronDown className="h-3.5 w-3.5 opacity-50 ml-auto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">{t('shareModal.roleEditor', 'Editor')}</SelectItem>
                      <SelectItem value="commenter">{t('shareModal.roleCommenter', 'Comentarista')}</SelectItem>
                      <SelectItem value="viewer">{t('shareModal.roleViewer', 'Visualizador')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={isLoading || !inviteEmail.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5 mr-2" />
                )}
                {t('shareModal.sendInvite', 'Enviar convite')}
              </Button>
            </form>
          )}

          {/* Members list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('shareModal.peopleWithAccess', 'Pessoas com acesso')}
              </p>
              {isFetching && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>

            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
              {/* Owner entry */}
              {owner && (
                <MemberRow
                  name={owner.name}
                  email={owner.email}
                  role="owner"
                  status="active"
                  isCurrentUser={owner.id === user?.id}
                  isOwner={true}
                  canManage={false}
                />
              )}

              {/* Member entries */}
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  name={member.name}
                  email={member.email}
                  role={member.role}
                  status={member.status}
                  isCurrentUser={member.userId === user?.id}
                  isOwner={false}
                  canManage={isOwner}
                  onRoleChange={(role) => handleRoleChange(member.id, role)}
                  onRemove={() => handleRemove(member.id, member.email)}
                />
              ))}

              {members.length === 0 && !isFetching && (
                <p className="text-sm text-muted-foreground text-center py-4 italic">
                  {t('shareModal.noCollaboratorsYet', 'Nenhum colaborador ainda.')}
                </p>
              )}
            </div>
          </div>

          {/* Role legend */}
          <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t('shareModal.roles', 'Papéis')}</p>
            <div className="grid grid-cols-3 gap-1 text-xs text-muted-foreground">
              <div>
                <span className="font-medium text-blue-600 dark:text-blue-400">{t('shareModal.roleEditor', 'Editor')}</span>
                <p>{t('shareModal.roleEditorDescription', 'Leitura + edição')}</p>
              </div>
              <div>
                <span className="font-medium text-amber-700 dark:text-amber-400">{t('shareModal.roleCommenter', 'Comentarista')}</span>
                <p>{t('shareModal.roleCommenterDescription', 'Leitura + comentários')}</p>
              </div>
              <div>
                <span className="font-medium">{t('shareModal.roleViewer', 'Visualizador')}</span>
                <p>{t('shareModal.roleViewerDescription', 'Somente leitura')}</p>
              </div>
            </div>
          </div>

          {/* Leave button — only for non-owners who are active members */}
          {isMember && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-destructive border-destructive/30 hover:bg-destructive/5 hover:border-destructive/60"
              onClick={handleLeave}
            >
              <LogOut className="h-3.5 w-3.5 mr-2" />
              {t('shareModal.leaveProject', 'Sair do projeto')}
            </Button>
          )}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ── MemberRow ─────────────────────────────────────────────────────────────────

interface MemberRowProps {
  name: string;
  email: string;
  role: MemberRole | 'owner';
  status: string;
  isCurrentUser: boolean;
  isOwner: boolean;
  canManage: boolean;
  onRoleChange?: (role: MemberRole) => void;
  onRemove?: () => void;
}

function MemberRow({
  name,
  email,
  role,
  status,
  isCurrentUser,
  isOwner,
  canManage,
  onRoleChange,
  onRemove,
}: MemberRowProps) {
  const { t } = useTranslation();
  const roleLabels = getRoleLabels(t);
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg p-2.5 transition-colors',
        isCurrentUser ? 'bg-primary/5 border border-primary/10' : 'hover:bg-muted/30'
      )}
    >
      {/* Avatar */}
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        {isOwner ? (
          <Crown className="h-3.5 w-3.5 text-primary" />
        ) : (
          <span className="text-xs font-semibold text-primary">{initials}</span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {name}
          {isCurrentUser && (
            <span className="ml-1.5 text-xs text-muted-foreground font-normal">{t('shareModal.youSuffix', '(você)')}</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground truncate">{email}</p>
      </div>

      {/* Role / status */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {status === 'invited' && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 h-5 bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
          >
            {t('shareModal.statusInvited', 'Aguardando')}
          </Badge>
        )}
        {status === 'declined' && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 h-5 bg-destructive/10 text-destructive border-destructive/20"
          >
            {t('shareModal.statusDeclined', 'Recusado')}
          </Badge>
        )}

        {canManage && role !== 'owner' ? (
          <Select
            value={role}
            onValueChange={(v) => onRoleChange?.(v as MemberRole)}
          >
            <SelectTrigger className={cn('h-6 text-xs px-2 border rounded-md', ROLE_COLORS[role])}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">{t('shareModal.roleEditor', 'Editor')}</SelectItem>
              <SelectItem value="commenter">{t('shareModal.roleCommenter', 'Comentarista')}</SelectItem>
              <SelectItem value="viewer">{t('shareModal.roleViewer', 'Visualizador')}</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge
            variant="outline"
            className={cn('text-[10px] px-1.5 py-0 h-5', ROLE_COLORS[role])}
          >
            {roleLabels[role]}
          </Badge>
        )}

        {canManage && role !== 'owner' && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Suppress unused import warning
void STATUS_LABELS;
